import {
  CalendarDays,
  FileImage,
  FileSearch,
  Hammer,
  Home,
  ListPlus,
  Package,
  Plus,
  Receipt,
  ScanLine,
  StickyNote,
  Target,
  Users,
} from 'lucide-react-native';
import type { ComponentType } from 'react';

import { Palette } from '@/constants/theme';

/**
 * Definitions for the Quick Actions sheet that opens from the center "+"
 * FAB. Mirrors the shape of `quick-toolbar.ts` so future customization
 * patterns can be shared. Users can hide / reorder via the dedicated
 * `/quick-actions` screen; the saved order is `property.quick_actions_keys_json`.
 */
export interface QuickActionMenuItem {
  key: string;
  label: string;
  sub: string;
  route: string;
  /** Background of the circular icon chip. */
  color: string;
  Icon: ComponentType<{ size?: number; color?: string; strokeWidth?: number }>;
}

export const QUICK_ACTIONS_MENU: QuickActionMenuItem[] = [
  // Wave 61 color pass (user call): Smart scan wears FOREST — matching
  // its dark-green active tile on the page toolbars — and the default
  // six stay distinct: moss, forest, honey / clay, lake, honeyDeep.
  { key: 'system',    label: 'Add system',    sub: 'Equipment, fireplaces, mowers', route: '/system/new',        color: Palette.moss,       Icon: Plus },
  { key: 'custom',    label: 'Add task',      sub: 'One-off or recurring chore',     route: '/custom-task/none',  color: Palette.honey,      Icon: ListPlus },
  { key: 'inventory', label: 'Add inventory', sub: 'Electronics, jewelry, tools',    route: '/inventory/new',     color: Palette.forestLight, Icon: Package },
  { key: 'expense',   label: 'Log expense',   sub: 'Bills, taxes, projects',         route: '/expenses/new',      color: Palette.lake,       Icon: Receipt },
  { key: 'quote',     label: 'Save a quote',  sub: 'Scan estimates, compare bids',   route: '/quotes/new',        color: Palette.forestLight, Icon: FileSearch },
  // Short label on the 3-across sheet tile ("AI Smart Scan" truncated
  // at phone width); the Tools grid + scan page carry the full name.
  { key: 'scan',      label: 'AI Scan',       sub: 'Any paper — read & auto-filed',  route: '/scan',              color: Palette.forest,     Icon: ScanLine },
  { key: 'note',      label: 'Notes',         sub: 'Free-form jottings & lists',     route: '/notes',             color: Palette.honeyDeep,  Icon: StickyNote },
  { key: 'contact',   label: 'Add contact',   sub: 'Plumber, HVAC, electrician',     route: '/contact/new',       color: Palette.clay,       Icon: Users },
  { key: 'pro',       label: 'Find a Pro',    sub: 'Local search by trade',          route: '/pros',              color: Palette.clay,       Icon: Hammer },
  { key: 'media',     label: 'Files',         sub: 'Photos, manuals, receipts',      route: '/media',             color: Palette.forestLight, Icon: FileImage },
  { key: 'goals',     label: 'Projects',      sub: 'Active projects + dream list',   route: '/projects',          color: Palette.honey,      Icon: Target },
  // Replaces the old "Search" action — Systems is the more useful
  // deep tool. The header's global Search button still reaches /search.
  { key: 'systems',   label: 'Systems',       sub: 'Search, filter, manage every system',  route: '/systems',           color: Palette.forest,     Icon: Home },
  { key: 'schedule',  label: 'Open schedule', sub: 'See all upcoming tasks',         route: '/(tabs)/schedule',   color: Palette.honeyDeep,  Icon: CalendarDays },
];

/** First 6 actions show by default — fits the 2×3 grid cleanly.
 *  Wave 60 (user call): Smart scan already ingests receipts, quotes,
 *  and inventory paperwork, so "Save a quote" and "Add inventory"
 *  retire from the defaults in favor of things scan CAN'T do —
 *  contacts and notes. "Log expense" stays: plenty of expenses have
 *  no paper at all (online bills, autopay). Everything retired is
 *  still one tap away in the quick-actions editor. Scan keeps the
 *  center-top hero slot from Wave 59. */
export const DEFAULT_MENU_KEYS: string[] = [
  'system', 'scan', 'custom', 'contact', 'expense', 'note',
];

export function parseMenuKeys(raw: string | null | undefined): string[] {
  if (!raw) return DEFAULT_MENU_KEYS;
  try {
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return DEFAULT_MENU_KEYS;
    const valid = new Set(QUICK_ACTIONS_MENU.map((a) => a.key));
    const cleaned = (arr as unknown[])
      .filter((k): k is string => typeof k === 'string')
      .filter((k) => valid.has(k));
    if (cleaned.length === 0) return DEFAULT_MENU_KEYS;
    // NOTE: previously we auto-appended any action missing from the user's
    // saved list. That had a worse side effect: removing an action via the
    // editor never stuck — the next read re-added it. Trust the user's
    // saved list now; new shortcuts go undiscovered until added manually,
    // which is the smaller harm.
    return cleaned;
  } catch {
    return DEFAULT_MENU_KEYS;
  }
}

export function getMenuItemByKey(key: string): QuickActionMenuItem | null {
  return QUICK_ACTIONS_MENU.find((a) => a.key === key) ?? null;
}
